(() => {
  'use strict';

  const ATTRIBUTION_KEY = 'gyl_ads_attribution_v1';
  const PENDING_LEAD_KEY = 'gyl_pending_lead_v1';
  const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'];
  const PHONE_CLICK_CONVERSION_DESTINATION = 'AW-17723566194/r95pCPHd188cEPLQoINC';
  const SAFE_STORAGE = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (_) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (_) {
        // Attribution still works for the current page when storage is unavailable.
      }
    }
  };

  const SAFE_SESSION = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (_) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (_) {
        // Non-fatal: success deduplication can fall back to page state only.
      }
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (_) {
        // Non-fatal.
      }
    }
  };

  function parseJson(value, fallback = {}) {
    if (!value) return fallback;
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch (_) {
      return fallback;
    }
  }

  function pushEvent(eventName, payload = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...payload
    });
  }

  function reportPhoneClickConversion() {
    window.dataLayer = window.dataLayer || [];
    const gtag = typeof window.gtag === 'function'
      ? window.gtag
      : function () {
          window.dataLayer.push(arguments);
        };

    gtag('event', 'conversion', {
      send_to: PHONE_CLICK_CONVERSION_DESTINATION,
      value: 1.0,
      currency: 'INR'
    });
  }

  function pageContext() {
    const body = document.body;
    const productTitle = document.querySelector('main h1')?.textContent?.trim() || '';
    const sku = document.querySelector('[id^="Sku-"]')?.textContent?.trim() || '';

    return {
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_type: body?.classList.contains('template-product') ? 'product' :
        body?.classList.contains('template-collection') ? 'collection' :
        body?.classList.contains('template-page') ? 'page' : 'other',
      product_name: productTitle,
      product_sku: sku
    };
  }

  function captureAttribution() {
    const params = new URLSearchParams(window.location.search);
    const stored = parseJson(SAFE_STORAGE.get(ATTRIBUTION_KEY), {});
    let changed = false;

    if (!stored.first_landing_page) {
      stored.first_landing_page = window.location.href;
      stored.first_referrer = document.referrer || '';
      stored.first_visit_at = new Date().toISOString();
      changed = true;
    }

    CLICK_ID_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        stored[key] = value;
        stored[`${key}_captured_at`] = new Date().toISOString();
        changed = true;
      }
    });

    if (changed) SAFE_STORAGE.set(ATTRIBUTION_KEY, JSON.stringify(stored));
    return stored;
  }

  function generateLeadReference() {
    const date = new Date().toISOString().slice(0, 10).replaceAll('-', '');
    let token = '';

    if (window.crypto?.randomUUID) {
      token = window.crypto.randomUUID().replaceAll('-', '').slice(0, 12).toUpperCase();
    } else {
      token = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`.slice(0, 12).toUpperCase();
    }

    return `GYL-${date}-${token}`;
  }

  function upsertHiddenInput(form, field, value) {
    if (!value) return;
    const name = `contact[${field}]`;
    let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    input.value = String(value);
  }

  function getLeadReference(form) {
    const existing = form.querySelector('input[name="contact[lead_reference]"]')?.value;
    if (existing) return existing;

    const pending = parseJson(SAFE_SESSION.get(PENDING_LEAD_KEY), {});
    if (pending.lead_reference) return pending.lead_reference;

    return generateLeadReference();
  }

  function enrichBulkInquiryForm(form) {
    const attribution = captureAttribution();
    const context = pageContext();
    const leadReference = getLeadReference(form);

    upsertHiddenInput(form, 'lead_reference', leadReference);
    upsertHiddenInput(form, 'page_url', window.location.href);
    upsertHiddenInput(form, 'page_path', window.location.pathname);
    upsertHiddenInput(form, 'first_landing_page', attribution.first_landing_page || '');
    upsertHiddenInput(form, 'first_referrer', attribution.first_referrer || '');
    upsertHiddenInput(form, 'product_sku', context.product_sku || '');

    CLICK_ID_KEYS.forEach((key) => {
      upsertHiddenInput(form, key, attribution[key] || '');
    });

    return leadReference;
  }

  function bindBulkInquiryForm() {
    const form = document.getElementById('bulk-inquiry-form');
    if (!form || form.dataset.gylAttributionBound === 'true') return;

    form.dataset.gylAttributionBound = 'true';
    enrichBulkInquiryForm(form);

    form.addEventListener('submit', () => {
      const leadReference = enrichBulkInquiryForm(form);
      const quantity = form.querySelector('#bulk_quantity')?.value || '';
      const productName = form.querySelector('#bulk_product')?.value?.trim() || pageContext().product_name;

      SAFE_SESSION.set(PENDING_LEAD_KEY, JSON.stringify({
        lead_reference: leadReference,
        product_name: productName,
        quantity,
        submitted_at: new Date().toISOString(),
        page_location: window.location.href
      }));

      pushEvent('submit_inquiry', {
        lead_reference: leadReference,
        enquiry_type: 'bulk_inquiry',
        product_name: productName,
        quantity,
        ...pageContext()
      });
    });
  }

  function bindBulkInquiryOpen() {
    if (typeof window.openBulkInquiry !== 'function' || window.openBulkInquiry.__gylAttributionWrapped) return;

    const originalOpen = window.openBulkInquiry;
    const wrapped = function (...args) {
      const productName = typeof args[0] === 'string' ? args[0] : '';
      const form = document.getElementById('bulk-inquiry-form');
      const leadReference = form ? enrichBulkInquiryForm(form) : generateLeadReference();

      pushEvent('bulk_inquiry_click', {
        lead_reference: leadReference,
        enquiry_type: 'bulk_inquiry',
        product_name: productName || pageContext().product_name,
        ...pageContext()
      });

      return originalOpen.apply(this, args);
    };

    wrapped.__gylAttributionWrapped = true;
    window.openBulkInquiry = wrapped;
  }

  function bindContactIntentEvents() {
    const contactToggle = document.getElementById('contact-toggle');
    if (contactToggle && contactToggle.dataset.gylAttributionBound !== 'true') {
      contactToggle.dataset.gylAttributionBound = 'true';
      contactToggle.addEventListener('click', () => {
        pushEvent('contact_options_click', {
          contact_method: 'contact_options',
          ...pageContext()
        });
      });
    }

    const selectors = [
      ['a.contact-btn.whatsapp, a[href*="wa.me"], a[href*="api.whatsapp.com"]', 'whatsapp_click', 'whatsapp'],
      ['a.contact-btn.call, a[href^="tel:"]', 'phone_click', 'phone'],
      ['a.contact-btn.email, a[href^="mailto:"]', 'email_click', 'email']
    ];

    selectors.forEach(([selector, eventName, method]) => {
      document.querySelectorAll(selector).forEach((link) => {
        if (link.dataset.gylAttributionBound === 'true') return;
        link.dataset.gylAttributionBound = 'true';
        link.addEventListener('click', () => {
          pushEvent(eventName, {
            contact_method: method,
            ...pageContext()
          });

          if (method === 'phone') {
            reportPhoneClickConversion();
          }
        });
      });
    });
  }

  function emitSuccessfulLeadOnce() {
    const success = document.getElementById('bulkInquirySuccess');
    if (!success) return;

    const pending = parseJson(SAFE_SESSION.get(PENDING_LEAD_KEY), {});
    const leadReference = pending.lead_reference || generateLeadReference();
    const dedupeKey = `gyl_lead_success_emitted_${leadReference}`;

    if (SAFE_SESSION.get(dedupeKey) === '1') return;
    SAFE_SESSION.set(dedupeKey, '1');

    const payload = {
      lead_reference: leadReference,
      enquiry_type: 'bulk_inquiry',
      product_name: pending.product_name || pageContext().product_name,
      quantity: pending.quantity || '',
      ...pageContext()
    };

    pushEvent('bulk_inquiry_submit_success', payload);
    pushEvent('generate_lead', payload);
    SAFE_SESSION.remove(PENDING_LEAD_KEY);
  }

  function init() {
    captureAttribution();
    bindBulkInquiryForm();
    bindBulkInquiryOpen();
    bindContactIntentEvents();
    emitSuccessfulLeadOnce();

    // Shopify sections and apps can inject or re-render controls after initial load.
    const observer = new MutationObserver(() => {
      bindBulkInquiryForm();
      bindBulkInquiryOpen();
      bindContactIntentEvents();
      emitSuccessfulLeadOnce();
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
